import React, { useEffect, useState } from "react";
import MetricCard from "./MetricCard";
import TrendChart from "./TrendChart";
import SectionLoader from "./SectionLoader";


const API_HOST = process.env.COCO_API_URL || "https://98.130.58.152.sslip.io/api/v1";
const API_BASE = `${API_HOST}/metrics/dashboard`;



function buildQuery(filters) {
    if (!filters) return "";
    const params = {
      country: filters.country || "",
      state: filters.state || "",
      district: filters.district || "",
      block: filters.block || "",
      village: filters.village || "",
      date_from: filters.startDate || "",
      date_to: filters.endDate || "",
    };
    return new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
  }

function CocoMetricsDashboard({ filters }) {
  const [summary, setSummary] = useState(null);
  const [yearly, setYearly] = useState(null);
  const [monthly, setMonthly] = useState(null);

  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingYearly, setLoadingYearly] = useState(true);
  const [loadingMonthly, setLoadingMonthly] = useState(true);

  const [errorSummary, setErrorSummary] = useState(false);
  const [errorYearly, setErrorYearly] = useState(false);
  const [errorMonthly, setErrorMonthly] = useState(false);

  async function apiGet(path, qs) {
    const res = await fetch(`${API_BASE}${path}?${qs}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    if (!res.ok) throw new Error("Failed to fetch " + path);
    return res.json();
  }

  useEffect(() => {
    if (!filters) return;

    // Reset current state & set loading indicators
    setSummary(null);
    setYearly(null);
    setMonthly(null);
    setErrorSummary(false);
    setErrorYearly(false);
    setErrorMonthly(false);
    setLoadingSummary(true);
    setLoadingYearly(true);
    setLoadingMonthly(true);

    const qs = buildQuery(filters);

    // 1. Fetch Summary
    apiGet("/summary", qs)
      .then((data) => setSummary(data))
      .catch(() => setErrorSummary(true))
      .finally(() => setLoadingSummary(false));

    // 2. Fetch Yearly Trend
    apiGet("/yearly-trend", qs)
      .then((data) => {
        const mapped = (data.yearly || []).map((d) => ({
          period: String(d.year),
          screening: d.unique_farmers_screened,
          adoption: d.unique_farmers_adopted,
        }));
        setYearly(mapped);
      })
      .catch(() => setErrorYearly(true))
      .finally(() => setLoadingYearly(false));

    // 3. Fetch Monthly Trend
    apiGet("/monthly-trend", qs)
      .then((data) => {
        const mapped = (data.monthly || []).map((d) => ({
          period: d.month,
          screening: d.unique_farmers_screened,
          adoption: d.unique_farmers_adopted,
        }));
        setMonthly(mapped);
      })
      .catch(() => setErrorMonthly(true))
      .finally(() => setLoadingMonthly(false));
  }, [filters]);

  const fmt = (n) => (n === null || n === undefined ? null : Number(n).toLocaleString());

  const genderPct = (rows, gender) => {
    if (!rows) return null;
    const row = rows.find((r) => r.gender === gender);
    return row ? row.pct ?? row.pct_of_adopted : null;
  };

  const cards = summary && [
    { label: "Number of Adoptions", value: fmt(summary.total_adoptions_by_farmer) },
    { label: "Number of Unique Adopters", value: fmt(summary.unique_farmers_adopters) },
    { label: "Unique Viewers of Screenings", value: fmt(summary.unique_viewers) },
    { label: "Total Viewers", value: fmt(summary.total_viewers) },

    { label: "Total Screenings", value: fmt(summary.total_screenings) },
    { label: "Total Videos Screened", value: fmt(summary.total_unique_videos_screened) },
    { label: "Number of Districts/Woreda Reached", value: fmt(summary.districts_reached) },
    { label: "Number of Blocks/Kebele Reached", value: fmt(summary.blocks_reached) },
    { label: "Number of Villages Reached", value: fmt(summary.villages_reached) },
    { label: "Number of Phone Numbers Registered", value: fmt(summary.unique_phone_numbers_registered) },

    { label: "Number of Videos Produced in selected period and region", value: fmt(summary.videos_produced) },
    { label: "Number of Development Groups Reached", value: fmt(summary.unique_development_groups_attended_screenings) },
    { label: "Average No of Screenings attended by Farmer", value: summary.avg_screenings_attended_by_farmer?.toFixed(2) },
    {
      label: "Gender Adopters Distribution",
      gender: {
        male: genderPct(summary.gender_adopters_distribution, "M"),
        female: genderPct(summary.gender_adopters_distribution, "F"),
      },
    },
    {
      label: "Gender Viewers Distribution",
      gender: {
        male: genderPct(summary.gender_viewers_distribution, "M"),
        female: genderPct(summary.gender_viewers_distribution, "F"),
      },
    },
  ];

  return (
    <div style={{ padding: 20 }}>
      {/* 1. Summary Cards Section */}
      <div style={{ marginBottom: 24 }}>
        {loadingSummary ? (
          <SectionLoader label="Loading Summary Metrics..." minHeight={180} />
        ) : errorSummary ? (
          <div style={{ padding: 16, background: "#fdf0ed", border: "1px solid #f5c6cb", borderRadius: 10, color: "#c0392b", fontSize: 13 }}>
            ⚠️ Failed to load summary metrics.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 16 }}>
            {cards && cards.map((c, i) => <MetricCard key={i} {...c} />)}
          </div>
        )}
      </div>

      {/* 2. Charts Section */}
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {/* Yearly Trend Section */}
        {loadingYearly ? (
          <SectionLoader label="Loading Yearly Trend Chart..." minHeight={260} />
        ) : errorYearly ? (
          <div style={{ padding: 16, background: "#fdf0ed", border: "1px solid #f5c6cb", borderRadius: 10, color: "#c0392b", fontSize: 13 }}>
            ⚠️ Failed to load yearly trend chart.
          </div>
        ) : (
          <TrendChart title="Unique Farmers: Screening vs Adoption (by Year)" data={yearly} />
        )}

        {/* Monthly Trend Section */}
        {loadingMonthly ? (
          <SectionLoader label="Loading Monthly Trend Chart..." minHeight={260} />
        ) : errorMonthly ? (
          <div style={{ padding: 16, background: "#fdf0ed", border: "1px solid #f5c6cb", borderRadius: 10, color: "#c0392b", fontSize: 13 }}>
            ⚠️ Failed to load monthly trend chart.
          </div>
        ) : (
          <TrendChart title="Unique Farmers: Screening vs Adoption (by Month)" data={monthly} />
        )}
      </div>
    </div>
  );
}

export default CocoMetricsDashboard;